/**
 * Comandos de voz do sistema (spec 011, pedido do Leopoldo de 03/10/2026),
 * chamados pelo nome "Atlas" — o "Jarvis" é só do ditado (ver `matcher.ts`).
 *
 * Quem OUVE só pede: a escuta local (ao vivo desligado ou pausado) ou a
 * transcrição do ao vivo (ligado — aí a escuta fica pausada). O Ctrl+P do
 * controle de apresentação pede o "imprimir" direto (22/10/2026). Quem ATENDE é
 * quem está na tela e tem o que precisa:
 *   - "proximo"      → `NextPatientWatcher` (aviso de próximo paciente)
 *   - "mandarEntrar" → `VoiceCommandWatcher` (o "Mandar entrar" da recepção)
 *   - "agenda"       → `VoiceCommandWatcher` (navega)
 *   - "imprimir"     → `VoiceDictationModal` com a coluna aberta (só card completo)
 *   - "salvar"       → `VoiceDictationModal` com a coluna aberta e card na fila
 *                      (o "Registrar no prontuário" — pedido de 05/10/2026)
 *   - "pausar"       → `useLiveDictation` da sessão ao vivo ouvindo
 *   - "retomar"      → `useLiveDictation` da sessão ao vivo pausada
 *   - "parar"        → `useLiveDictation` da sessão ao vivo ligada (ou pausada)
 *   - "prontuario"   → `VoiceDictationModal` com a coluna aberta (o botão
 *                      "Prontuário"). Só pela caneta SEGURADA do controle
 *                      (24/10/2026) — não tem fala, nem entra na lista do Atlas.
 * Quem atende marca `atendido` na hora (o evento é síncrono). Ninguém
 * atendeu — "imprime" fora da ficha, "desliga" sem ao vivo — é bipe de
 * recusa: quem fala com o Atlas pode não estar olhando a tela.
 *
 * Fica de fora de propósito fechar o atendimento ("finaliza"): continua no
 * clique. Gravar no prontuário entrou em 05/10/2026 a pedido do Leopoldo
 * ("Atlas, salve"), só o que o botão da coluna já faz.
 */

import { useEffect, useRef } from 'react';
import { playVoiceTone } from '@/utils/chime';
import type { PrintTarget } from '../live';

export const COMANDO_VOZ_EVENT = 'dratento:voz:comando';

export type ComandoSistema =
  | 'proximo'
  | 'mandarEntrar'
  | 'agenda'
  | 'imprimir'
  | 'salvar'
  | 'pausar'
  | 'retomar'
  | 'parar'
  | 'prontuario';
/**
 * `escuta` = reconhecedor local ("Atlas"/"Jarvis"); `ao-vivo` = transcrição do
 * ao vivo; `tecla` = botão do controle de apresentação (`controle.ts`).
 */
export type ComandoOrigem = 'escuta' | 'ao-vivo' | 'tecla';

export interface PedidoComando {
  comando: ComandoSistema;
  origem: ComandoOrigem;
  /** Só no "imprimir". */
  alvos?: PrintTarget[];
  /** Quem atende marca. */
  atendido: boolean;
  /** Quem atende pode dizer se deu certo (o brilho do "Atlas" mostra). */
  resultado?: Promise<boolean>;
}

/** O que quem atende devolve: nada (deu), ou se deu certo — na hora ou depois. */
export type RespostaComando = void | boolean | Promise<boolean | void>;

/** Quantos atendem cada comando agora — pra lista do "Atlas" mostrar só o que vale nesta tela. */
const atendentes = new Map<ComandoSistema, number>();
type ComandoFalado = Exclude<ComandoSistema, 'prontuario'>;
const ORDEM: ComandoFalado[] = [
  'proximo',
  'mandarEntrar',
  'agenda',
  'imprimir',
  'salvar',
  'pausar',
  'retomar',
  'parar',
];

/** Os comandos que têm quem atenda nesta tela, na ordem da lista. */
export function comandosComQuemAtenda(): ComandoFalado[] {
  return ORDEM.filter((c) => (atendentes.get(c) ?? 0) > 0);
}

/**
 * Pede o comando; devolve se alguém na tela atendeu (senão já tocou o bipe de
 * recusa). `aoTerminar` recebe se deu certo, quando quem atendeu terminar.
 */
export function pedirComando(
  p: Omit<PedidoComando, 'atendido' | 'resultado'>,
  opts?: { aoTerminar?: (ok: boolean) => void },
): boolean {
  const detail: PedidoComando = { ...p, atendido: false };
  window.dispatchEvent(new CustomEvent(COMANDO_VOZ_EVENT, { detail }));
  const fim = opts?.aoTerminar;
  if (!detail.atendido) {
    console.info('[voz] comando "%s" sem quem atenda nesta tela', p.comando);
    playVoiceTone('encerrado');
    fim?.(false);
    return false;
  }
  if (fim) {
    void Promise.resolve(detail.resultado ?? true).then(
      (ok) => fim(ok),
      () => fim(false),
    );
  }
  return true;
}

export const ATLAS_TECLA_EVENT = 'dratento:voz:atlas-tecla';

/**
 * Tecla do Atlas (controle, 22/10/2026): vale como dizer "Atlas" agora. Quem
 * ouve o pedido é quem estiver de pé: o ao vivo ouvindo (`useLiveDictation`)
 * ou a escuta local (`WakeWordWatcher`) — que a tecla liga só pro pedido,
 * mesmo com a escuta desligada nesta máquina. Ninguém = bipe de recusa.
 */
export function pedirAtlasPorTecla(): boolean {
  const detail = { atendido: false };
  window.dispatchEvent(new CustomEvent(ATLAS_TECLA_EVENT, { detail }));
  if (!detail.atendido) {
    console.info('[voz] tecla do Atlas sem quem ouça nesta tela');
    playVoiceTone('encerrado');
  }
  return detail.atendido;
}

/** Ouve a tecla do Atlas enquanto `ativo` (o primeiro que pega, atende). */
export function useAtlasPorTecla(handler: () => void, ativo: boolean): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!ativo) return;
    const ouvir = (e: Event) => {
      const p = (e as CustomEvent<{ atendido: boolean }>).detail;
      if (!p || p.atendido) return;
      p.atendido = true;
      ref.current();
    };
    window.addEventListener(ATLAS_TECLA_EVENT, ouvir);
    return () => window.removeEventListener(ATLAS_TECLA_EVENT, ouvir);
  }, [ativo]);
}

/**
 * Atende UM comando enquanto `ativo`. O handler roda com a versão atual (ref),
 * então quem atende pode depender de estado sem re-registrar a cada render.
 * Devolver `false` (ou uma promessa de `false`) = não deu.
 */
export function useComandoVoz(
  comando: ComandoSistema,
  handler: (p: PedidoComando) => RespostaComando,
  ativo = true,
): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!ativo) return;
    const ouvir = (e: Event) => {
      const p = (e as CustomEvent<PedidoComando>).detail;
      if (!p || p.comando !== comando || p.atendido) return;
      p.atendido = true;
      const r = ref.current(p);
      if (r !== undefined) p.resultado = Promise.resolve(r).then((ok) => ok !== false);
    };
    window.addEventListener(COMANDO_VOZ_EVENT, ouvir);
    atendentes.set(comando, (atendentes.get(comando) ?? 0) + 1);
    return () => {
      window.removeEventListener(COMANDO_VOZ_EVENT, ouvir);
      atendentes.set(comando, (atendentes.get(comando) ?? 1) - 1);
    };
  }, [comando, ativo]);
}
